import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import ResourceManager from "./components/ResourceManager";
import { CONTENT_TYPES } from "@/lib/constants";

const useEditionOptions = () => {
  const { data = [] } = useQuery({ queryKey: ["admin-editions"], queryFn: async () => (await api.get("/admin/editions")).data });
  return data.map((e) => ({ value: e.id, label: `${e.date_label} — ${e.city}` }));
};
const useSpeakerOptions = () => {
  const { data = [] } = useQuery({ queryKey: ["admin-speakers"], queryFn: async () => (await api.get("/admin/speakers")).data });
  return data.map((s) => ({ value: s.id, label: `${s.name} (${s.company})` }));
};
const useConfOptions = () => {
  const { data = [] } = useQuery({ queryKey: ["admin-conferences"], queryFn: async () => (await api.get("/admin/conferences")).data });
  return data.map((c) => ({ value: c.id, label: c.title }));
};

export const EditionsAdmin = () => (
  <ResourceManager
    title="Éditions" subtitle="Gérez chaque édition et ses contenus" endpoint="editions"
    columns={[
      { key: "date_label", label: "Édition" }, { key: "city", label: "Ville" },
      { key: "theme", label: "Thème" }, { key: "status", label: "Statut", type: "badge" },
      { key: "is_active", label: "Active", render: (it) => (it.is_active ? "✓" : "—") },
    ]}
    fields={[
      { name: "year", label: "Année", type: "number", required: true },
      { name: "city", label: "Ville", type: "text" },
      { name: "date_label", label: "Libellé de date", type: "text", hint: "ex. 11 juin 2026" },
      { name: "date_iso", label: "Date ISO", type: "text", hint: "AAAA-MM-JJ (optionnel)" },
      { name: "venue", label: "Lieu (adresse complète)", type: "text", hint: "ex. Le Jardin Rêvé — 89 rue de Richelieu, Paris 2e" },
      { name: "schedule", label: "Horaires", type: "text", hint: "ex. 14h15 — 21h00" },
      { name: "slug", label: "Slug (URL)", type: "text", hint: "ex. 2026-paris" },
      { name: "theme", label: "Thème", type: "text" },
      { name: "summary", label: "Résumé", type: "textarea", rows: 5 },
      { name: "hero_image", label: "Image hero", type: "image" },
      { name: "status", label: "Statut", type: "select", options: [{ value: "past", label: "Passée" }, { value: "active", label: "Active (mise en avant)" }, { value: "upcoming", label: "À venir" }] },
      { name: "is_active", label: "Édition active du site", type: "checkbox", checkboxLabel: "Utilisée pour le dashboard & le hero" },
      { name: "registration_status", label: "Statut des inscriptions", type: "select", options: [{ value: "ferme", label: "Fermé (clôturé / passé)" }, { value: "preinscription", label: "Préinscription / information" }, { value: "ouvert", label: "Ouvert" }], hint: "Pilote automatiquement le bouton public (aucune modif de code par an)" },
      { name: "registration_url", label: "URL d'inscription (statut Ouvert)", type: "text", hint: "Laisser vide = /inscription (formulaire interne)" },
      { name: "registration_open", label: "Inscriptions ouvertes (hérité)", type: "checkbox", checkboxLabel: "Ignoré si un « Statut des inscriptions » est choisi ci-dessus" },
      { name: "registration_open_at", label: "Date d'ouverture des inscriptions", type: "text", hint: "AAAA-MM-JJ (optionnel — automatise l'ouverture)" },
      { name: "registration_close_at", label: "Date de fermeture des inscriptions", type: "text", hint: "AAAA-MM-JJ (optionnel — automatise la fermeture)" },
      { name: "registration_closed_message", label: "Message quand fermé", type: "textarea", rows: 2, hint: "Affiché quand les inscriptions sont closes" },
      { name: "capacity", label: "Capacité maximale (personnes)", type: "number", hint: "0 = illimité" },
      { name: "auto_waitlist_when_full", label: "Liste d'attente auto", type: "checkbox", checkboxLabel: "Placer en liste d'attente quand complet (sinon reste À valider)" },
      { name: "is_demo", label: "Donnée de démonstration", type: "checkbox", checkboxLabel: "Marquer comme démo/placeholder" },
      { name: "order", label: "Ordre d'affichage", type: "number" },
      { name: "gallery", label: "Galerie d'images", type: "gallery", hint: "Sélection multiple depuis la bibliothèque, ou coller une URL" },
    ]}
  />
);

export const SpeakersAdmin = () => {
  const editions = useEditionOptions();
  return (
    <ResourceManager
      title="Intervenants" subtitle="Fiches des intervenants" endpoint="speakers"
      options={{ editions }}
      columns={[
        { key: "photo", label: "", type: "image" }, { key: "name", label: "Nom" },
        { key: "function", label: "Fonction" }, { key: "company", label: "Société" },
      ]}
      fields={[
        { name: "name", label: "Nom", type: "text", required: true },
        { name: "function", label: "Fonction", type: "text" },
        { name: "company", label: "Société", type: "text" },
        { name: "bio", label: "Biographie", type: "textarea", rows: 6 },
        { name: "photo", label: "Photo (laisser vide = placeholder élégant)", type: "image" },
        { name: "slug", label: "Slug (URL)", type: "text" },
        { name: "edition_ids", label: "Éditions", type: "multiselect", optionsKey: "editions" },
        { name: "order", label: "Ordre", type: "number" },
      ]}
    />
  );
};

export const ConferencesAdmin = () => {
  const editions = useEditionOptions();
  const speakers = useSpeakerOptions();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const themes = (settings?.article_themes || []).map((t) => ({ value: t, label: t }));
  return (
    <ResourceManager
      title="Programme / Conférences" subtitle="Conférences, focus techniques et temps forts" endpoint="conferences"
      options={{ editions, speakers, themes }}
      columns={[
        { key: "order", label: "#" }, { key: "title", label: "Titre" },
        { key: "type", label: "Type", type: "badge" }, { key: "company", label: "Société" },
      ]}
      fields={[
        { name: "title", label: "Titre", type: "text", required: true },
        { name: "edition_id", label: "Édition", type: "select", optionsKey: "editions" },
        { name: "type", label: "Type", type: "select", options: [{ value: "conference", label: "Conférence" }, { value: "focus", label: "Focus technique" }, { value: "pause", label: "Pause networking" }, { value: "cocktail", label: "Cocktail" }] },
        { name: "order", label: "Ordre dans le programme", type: "number" },
        { name: "time_label", label: "Horaire", type: "text", hint: "ex. 14h15" },
        { name: "duration", label: "Durée", type: "text", hint: "ex. 15 min" },
        { name: "speaker_ids", label: "Intervenant(s)", type: "multiselect", optionsKey: "speakers" },
        { name: "company", label: "Société", type: "text" },
        { name: "summary", label: "Résumé", type: "textarea", rows: 5 },
        { name: "key_takeaway", label: "À retenir", type: "textarea", rows: 2 },
        { name: "theme_tags", label: "Thématiques", type: "multiselect", optionsKey: "themes", hint: "Liste commune administrable depuis Réglages → Thématiques (partagée avec les Articles)" },
        { name: "image", label: "Image", type: "image" },
        { name: "replay_url", label: "Lien replay", type: "text" },
        { name: "document_url", label: "Document associé", type: "document" },
      ]}
    />
  );
};

export const PartnersAdmin = () => {
  const editions = useEditionOptions();
  return (
    <ResourceManager
      title="Partenaires" subtitle="Logos, niveaux et éditions" endpoint="partners"
      options={{ editions }}
      columns={[
        { key: "logo", label: "", type: "image" }, { key: "name", label: "Nom" },
        { key: "level", label: "Niveau", type: "badge" },
        { key: "is_active", label: "Actif", render: (it) => (it.is_active ? "✓" : "—") },
      ]}
      fields={[
        { name: "name", label: "Nom", type: "text", required: true },
        { name: "logo", label: "Logo", type: "image" },
        { name: "level", label: "Niveau", type: "select", options: [{ value: "premium", label: "Premium" }, { value: "official", label: "Officiel" }, { value: "media", label: "Média" }] },
        { name: "website", label: "Site web", type: "text" },
        { name: "description", label: "Description", type: "textarea", rows: 3 },
        { name: "edition_ids", label: "Éditions", type: "multiselect", optionsKey: "editions" },
        { name: "is_active", label: "Actif", type: "checkbox", checkboxLabel: "Afficher sur le site" },
        { name: "order", label: "Ordre", type: "number" },
      ]}
    />
  );
};

export const ArticlesAdmin = () => {
  const editions = useEditionOptions();
  const speakers = useSpeakerOptions();
  const conferences = useConfOptions();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const themes = (settings?.article_themes || []).map((t) => ({ value: t, label: t }));
  const contentTypes = CONTENT_TYPES.map((t) => ({ value: t, label: t }));
  return (
    <ResourceManager
      title="Articles & Expertises" subtitle="Rubrique éditoriale — triée automatiquement du plus récent au plus ancien" endpoint="articles"
      enableDocSync
      options={{ editions, speakers, conferences, themes, contentTypes }}
      columns={[
        { key: "image", label: "", type: "image" }, { key: "title", label: "Titre" },
        { key: "content_type", label: "Type", type: "badge" },
        { key: "theme", label: "Thématique" }, { key: "status", label: "Statut", type: "badge" },
      ]}
      fields={[
        { name: "title", label: "Titre", type: "text", required: true, autoSlug: "slug" },
        { name: "slug", label: "Slug (URL) — généré depuis le titre, modifiable", type: "text", slugField: true },
        { name: "content_type", label: "Type de contenu", type: "select", optionsKey: "contentTypes" },
        { name: "theme", label: "Thématique", type: "select", optionsKey: "themes", hint: "Liste administrable depuis Réglages → Thématiques" },
        { name: "chapo", label: "Chapô", type: "textarea", rows: 3 },
        { name: "content", label: "Contenu", type: "textarea", rows: 10 },
        { name: "key_points", label: "Points clés", type: "tags" },
        { name: "image", label: "Image principale", type: "image" },
        { name: "gallery", label: "Galerie d'images", type: "gallery", hint: "Sélection multiple depuis la bibliothèque, ou coller une URL" },
        { name: "replay_url", label: "Lien replay", type: "text" },
        { name: "date", label: "Date", type: "text", hint: "AAAA-MM-JJ" },
        { name: "author", label: "Auteur / source", type: "text" },
        { name: "company", label: "Société", type: "text" },
        { name: "edition_id", label: "Édition liée", type: "select", optionsKey: "editions" },
        { name: "speaker_ids", label: "Intervenant(s)", type: "multiselect", optionsKey: "speakers" },
        { name: "conference_id", label: "Conférence liée", type: "select", optionsKey: "conferences" },
        { name: "seo_title", label: "SEO title (optionnel)", type: "text", hint: "Vide = titre de l'article repris automatiquement" },
        { name: "meta_description", label: "Meta description (optionnel)", type: "textarea", rows: 2, hint: "Vide = chapô repris automatiquement" },
        { name: "status", label: "Statut", type: "select", options: [{ value: "draft", label: "Brouillon" }, { value: "published", label: "Publié" }] },
        { name: "featured_home", label: "Mettre en avant sur la Home", type: "checkbox", checkboxLabel: "Afficher dans le bloc articles de la page d'accueil (si Publié)" },
        { name: "home_order", label: "Ordre de mise en avant (Home)", type: "number", hint: "Plus petit = affiché en premier" },
        { name: "buildinglab_label", label: "BuildingLab — libellé du lien", type: "text" },
        { name: "buildinglab_url", label: "BuildingLab — URL", type: "text" },
        { name: "google_doc_url", label: "Lien Google Doc source", type: "text", section: "advanced", hint: "Utilisé uniquement pour la synchronisation éditoriale automatique" },
      ]}
    />
  );
};
